import type { ArcgisMapView } from './arcgisView';

/** Mean Earth radius used to size a SceneView camera that fills the pane. */
export const EARTH_RADIUS_M = 6_371_000;

/** SceneView default vertical field of view when `camera.fov` is missing. */
export const DEFAULT_FOV_DEG = 55;

const WEB_MERCATOR_TILE_SIZE = 256;

/** Test/fake view; live views use `ArcgisMapView`. */
export type ZoomOutConstraintView = {
  type?: string;
  width?: number;
  height?: number;
  zoom?: number;
  destroyed?: boolean;
  camera?: {
    fov?: number | null;
    tilt?: number | null;
    heading?: number | null;
    position?: {
      longitude?: number | null;
      latitude?: number | null;
      z?: number | null;
    } | null;
  } | null;
  center?: { latitude?: number | null; longitude?: number | null } | null;
  constraints?: {
    altitude?: { max?: number | null } | null;
    minZoom?: number | null;
    geometry?: {
      type?: string;
      xmin?: number;
      xmax?: number;
      ymin?: number;
      ymax?: number;
    } | null;
  } | null;
  goTo?: (target: unknown, options?: { animate?: boolean }) => unknown;
};

type ConstraintTarget = ArcgisMapView | ZoomOutConstraintView;

function callGoTo(view: ConstraintTarget, target: unknown): void {
  const goTo = view.goTo as
    | ((target: unknown, options?: { animate?: boolean }) => unknown)
    | undefined;
  try {
    const result = goTo?.(target, { animate: false }) as
      | Promise<unknown>
      | unknown;
    if (
      result != null &&
      typeof (result as Promise<unknown>).then === 'function'
    ) {
      // Swallow goTo rejections (e.g. navigation not ready) so zoom-constraint
      // watches do not surface uncaught promise errors.
      void (result as Promise<unknown>).catch(() => undefined);
    }
  } catch {
    // Sync goTo failures must not break the constraint watch.
  }
}

/**
 * Max camera altitude (m) so Earth's disk covers the larger pane dimension.
 * Uses vertical FOV scaled by max(width, height) / height.
 */
export function maxAltitudeToFillViewport(
  width: number,
  height: number,
  fovDeg = DEFAULT_FOV_DEG
): number {
  const safeWidth = Math.max(1, width);
  const safeHeight = Math.max(1, height);
  const fovRad = (Math.max(1, fovDeg) * Math.PI) / 180;
  const needed = Math.min(
    Math.PI - 0.05,
    fovRad * (Math.max(safeWidth, safeHeight) / safeHeight)
  );
  const sinHalf = Math.sin(needed / 2);
  if (sinHalf <= 0) return EARTH_RADIUS_M;
  return EARTH_RADIUS_M / sinHalf - EARTH_RADIUS_M;
}

/**
 * Web Mercator cuts off near ±85.05°. Past that, a 2D view paints empty
 * background.
 */
const MAX_WEB_MERCATOR_LAT = 85.05112878;

/**
 * Fraction of the world height from this latitude to the nearer mercator edge.
 * 0.5 at the equator. Smaller as the camera nears a pole.
 */
export function mercatorEdgeFraction(latitude: number): number {
  const clamped = Math.max(
    -MAX_WEB_MERCATOR_LAT,
    Math.min(MAX_WEB_MERCATOR_LAT, latitude)
  );
  const rad = (clamped * Math.PI) / 180;
  const sin = Math.sin(rad);
  const y = 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI);
  const fromNorth = Math.min(1, Math.max(0, y));
  return Math.min(fromNorth, 1 - fromNorth);
}

/**
 * Min Web Mercator zoom so the world covers the pane, including the shorter
 * distance from the camera latitude to the nearer pole. A world that is only as
 * tall as the pane still leaves a black band when the camera is not on the
 * equator.
 */
export function minZoomToFillViewport(
  width: number,
  height: number,
  latitude?: number | null
): number {
  const span = Math.max(width, height, 1);
  const base = Math.max(
    0,
    Math.ceil(Math.log2(span / WEB_MERCATOR_TILE_SIZE) - 1e-9)
  );
  if (typeof latitude !== 'number' || !Number.isFinite(latitude)) return base;
  const edge = Math.max(mercatorEdgeFraction(latitude), 0.02);
  // 2% keeps rounding from leaving a pixel of background past the pole.
  const requiredWorld = ((height / 2) * 1.02) / edge;
  const latZoom = Math.log2(requiredWorld / WEB_MERCATOR_TILE_SIZE);
  if (!Number.isFinite(latZoom)) return base;
  // MapView.constraints.minZoom must be an integer LOD. A fractional value
  // throws in the setter and aborts view startup, leaving the pane blank.
  return Math.max(base, Math.ceil(latZoom - 1e-9));
}

/**
 * Latitude whose mercator row is `fromNorth` (0 at the north edge, 1 at the
 * south).
 */
function latitudeAtMercatorFromNorth(fromNorth: number): number {
  const y = Math.min(0.5, Math.max(0, fromNorth));
  const k = Math.exp((0.5 - y) * 4 * Math.PI);
  const sin = (k - 1) / (k + 1);
  return (Math.asin(Math.min(1, Math.max(-1, sin))) * 180) / Math.PI;
}

/**
 * Northernmost and southernmost center latitude that still keeps the mercator
 * world edge outside this pane. Panning past it paints empty background.
 */
export function clampLatitudeForViewport(
  latitude: number,
  zoom: number,
  height: number
): number {
  const world = WEB_MERCATOR_TILE_SIZE * 2 ** zoom;
  const half = (Math.max(1, height) / 2) * 1.02;
  if (!Number.isFinite(world) || half * 2 >= world) return 0;
  const maxLat = latitudeAtMercatorFromNorth(half / world);
  if (!Number.isFinite(maxLat)) return latitude;
  return Math.max(-maxLat, Math.min(maxLat, latitude));
}

/** Apply SDK zoom-out caps and pull the camera in if it is currently too far. */
export function applyZoomOutConstraints(view: ConstraintTarget): void {
  if (view.destroyed) return;
  const width = view.width ?? 0;
  const height = view.height ?? 0;
  if (width <= 0 || height <= 0) return;

  if (view.type === '3d') {
    const camera = 'camera' in view ? view.camera : undefined;
    const fov = camera?.fov ?? DEFAULT_FOV_DEG;
    const maxAltitude = maxAltitudeToFillViewport(width, height, fov);
    const constraints = view.constraints as {
      altitude?: { max?: number | null } | null;
    } | null;
    if (constraints) {
      if (!constraints.altitude) {
        constraints.altitude = {};
      }
      constraints.altitude.max = maxAltitude;
    }
    const position = camera?.position;
    const z = position && 'z' in position ? position.z : undefined;
    const longitude =
      position && 'longitude' in position ? position.longitude : undefined;
    const latitude =
      position && 'latitude' in position ? position.latitude : undefined;
    if (
      typeof z === 'number' &&
      z > maxAltitude &&
      typeof longitude === 'number' &&
      typeof latitude === 'number'
    ) {
      callGoTo(view, {
        position: {
          longitude,
          latitude,
          z: maxAltitude,
        },
        tilt: camera?.tilt ?? 0,
        heading: camera?.heading ?? 0,
      });
    }
    return;
  }

  const currentZoom = view.zoom;
  if (
    typeof currentZoom !== 'number' ||
    !Number.isFinite(currentZoom) ||
    currentZoom < 0
  ) {
    return;
  }

  const latitude =
    view.center && typeof view.center.latitude === 'number'
      ? view.center.latitude
      : null;
  const minZoom = minZoomToFillViewport(width, height, latitude);
  const mapConstraints = view.constraints as {
    minZoom?: number | null;
    geometry?: { ymax?: number | null } | null;
  } | null;
  // Bound the center at the zoom on screen. A drag cancels goTo, and a bound
  // computed for a higher zoom still lets this frame slide past the pole.
  const maxCenterLat = clampLatitudeForViewport(
    MAX_WEB_MERCATOR_LAT,
    currentZoom,
    height
  );
  if (mapConstraints) {
    mapConstraints.minZoom = minZoom;
    const previousMax = mapConstraints.geometry?.ymax;
    if (previousMax == null || Math.abs(previousMax - maxCenterLat) > 0.02) {
      try {
        mapConstraints.geometry = {
          type: 'extent',
          xmin: -180,
          xmax: 180,
          ymin: -maxCenterLat,
          ymax: maxCenterLat,
          spatialReference: { wkid: 4326 },
        } as typeof mapConstraints.geometry;
      } catch {
        // A rejected extent must not skip the camera pull below.
      }
    }
  }
  const longitude =
    view.center && typeof view.center.longitude === 'number'
      ? view.center.longitude
      : null;
  const targetZoom = Math.max(currentZoom, minZoom);
  const clampedLat =
    typeof latitude === 'number'
      ? clampLatitudeForViewport(latitude, targetZoom, height)
      : null;
  const zoomNeedsPull = currentZoom < minZoom;
  const latNeedsPull =
    typeof latitude === 'number' &&
    typeof clampedLat === 'number' &&
    Math.abs(clampedLat - latitude) > 0.05;
  if (!zoomNeedsPull && !latNeedsPull) return;
  callGoTo(view, {
    ...(zoomNeedsPull ? { zoom: targetZoom } : {}),
    ...(latNeedsPull && typeof longitude === 'number'
      ? { center: [longitude, clampedLat] }
      : {}),
  });
}
